/**
 * useInstallPrompt
 *
 * Captures the browser's `beforeinstallprompt` event so we can show our own
 * branded "Install App" button at a time of our choosing, instead of relying
 * on the browser's default banner.
 *
 * Returns:
 *   installable  {boolean}   true when the browser has a deferred prompt ready
 *   isInstalled  {boolean}   true when running inside the installed PWA shell
 *   promptInstall {Function} call this from a button onClick — shows the prompt
 *                            and resolves to "accepted" | "dismissed" | null
 *
 * Usage:
 *   const { installable, promptInstall } = useInstallPrompt();
 *   {installable && (
 *     <button onClick={promptInstall}>Install MechOnGo Automobiles App</button>
 *   )}
 */

import { useState, useEffect, useCallback } from "react";

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installable, setInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect if already running as an installed PWA (standalone / fullscreen)
    const mq = window.matchMedia("(display-mode: standalone)");
    setIsInstalled(
      mq.matches ||
        // iOS Safari sets this on the navigator object
        navigator.standalone === true,
    );

    const onDisplayModeChange = (e) => setIsInstalled(e.matches);
    mq.addEventListener("change", onDisplayModeChange);

    // Capture the deferred install prompt
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Stop the browser's automatic mini-infobar
      setDeferredPrompt(e);
      setInstallable(true);
    };

    // Fires when the user installs the app (from our prompt or the browser UI)
    const onAppInstalled = () => {
      setInstallable(false);
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      mq.removeEventListener("change", onDisplayModeChange);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  /**
   * Show the browser install dialog.
   * @returns {Promise<"accepted"|"dismissed"|null>}
   */
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    // The prompt can only be used once — discard it afterwards.
    setDeferredPrompt(null);
    setInstallable(false);

    return outcome; // "accepted" | "dismissed"
  }, [deferredPrompt]);

  return { installable, isInstalled, promptInstall };
}
